import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ManagementComponent } from '../management.component';
import { B2BService } from 'src/app/b2-b.service';
import { SharedService } from 'src/app/shared.service';
import { ManagementServiceService } from 'src/app/management-service.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { IdObfuscator } from 'src/app/utils/id-obfuscator.util';

// Interface matching backend ProductClaimProof class
export interface ProductClaimProof {
  ID: number;
  FKClaimProductID: number;
  UploadedDocument: any; // byte[] - not used in frontend
  Base64UploadedDocument: string; // ✅ MUST match C# property name (singular)
  FileName?: string; // For UI display
}

// Interface matching backend ClaimProduct class
export interface ClaimProduct {
  FKClaimID: number;
  DBID: number;
  DocumentID: string;
  IndividualProductDocumentID: string;
  Name: string;
  Code: string;
  Description: string;
  DescriptionRows: any[]; // Array of description rows
  ClaimedQuantity: number;
  DocQuantity: number;
  ClaimType: any;
  UserClaimDescription: string; // User's description of the problem
  Proofs: ProductClaimProof[];
  Status: any;
  Solution: any;
}

export interface ProductValidationError {
  product: ClaimProduct;
  missingClaimType: boolean;
  missingDescription: boolean;
  missingProof: boolean;
}

// Interface for Claim Product Solution
export interface ClaimProductSolution {
  ID: number;
  FKClaimProductID: number;
  Description: string;
}

@Component({
  selector: 'app-claims',
  templateUrl: './claims.component.html',
  styleUrls: ['./claims.component.css'],
  animations: [
    trigger('slideDown', [
      transition(':enter', [
        style({ height: '0', opacity: '0', overflow: 'hidden' }),
        animate('300ms ease-out', style({ height: '*', opacity: '1' }))
      ]),
      transition(':leave', [
        style({ height: '*', opacity: '1', overflow: 'hidden' }),
        animate('300ms ease-in', style({ height: '0', opacity: '0' }))
      ])
    ])
  ]
})
export class ClaimsComponent implements OnInit {

  // Invoice search input reference
  @ViewChild('invoiceSearchInput') invoiceSearchInput!: ElementRef<HTMLInputElement>;
  invoiceSearchHighlighted: boolean = false;

  // Invoice search
  invoiceSearchTerm: string = '';
  invoiceSearchTermUpdate = new Subject<string>();
  searchedInvoices: any[] = [];
  showInvoiceResults: boolean = false;
  loadingInvoices: boolean = false;
  selectedInvoice: any = null;

  // Reception search (for External claims)
  @ViewChild('receptionSearchInput') receptionSearchInput!: ElementRef<HTMLInputElement>;
  receptionSearchHighlighted: boolean = false;
  receptionSearchTerm: string = '';
  receptionSearchTermUpdate = new Subject<string>();
  searchedReceptions: any[] = [];
  showReceptionResults: boolean = false;
  loadingReceptions: boolean = false;
  selectedReception: any = null;

  // Claim products (from selected invoice or reception)
  claimProducts: ClaimProduct[] = [];
  productClaimTypes: any[] = [];
  loadingProducts: boolean = false;
  openClaimTypeDropdownIndex: number = -1; // Track which product's dropdown is open
  productsExpanded: boolean = true; // Track if products section is expanded

  // Claim form fields
  claimTitle: string = '';

  // File upload (for product proofs)
  acceptedFileTypes: string = '.jpg,.jpeg,.mp4';

  // Submission
  submitting: boolean = false;
  submitSuccess: boolean = false;
  submitError: string = '';

  // Product validation errors
  productValidationErrors: ProductValidationError[] = [];
  showProductValidationErrors: boolean = false;

  // Tab switching
  showNewClaimTab: boolean = false; // false = My Claims, true = New Claim

  // My Claims list
  claims: any[] = [];
  Filter: any = {};
  loadingClaims: boolean = false;

  // Partner filter (for My Claims tab)
  partnerSearchTerm: string = '';
  partnerSearchTermUpdate = new Subject<string>();
  searchedPartners: any[] = [];
  showPartnerResults: boolean = false;
  loadingPartners: boolean = false;

  // Status filter dropdown (for My Claims tab)
  showStatusFilterDropdown: boolean = false;

  // New Claim - Claim Type Selection (Step 0a)
  selectedClaimGeneralType: any = null; // The selected claim general type (internal/external)

  // Partner selection (for New Claim tab - separate from My Claims filter) (Step 0b)
  newClaimPartnerSearchTerm: string = '';
  newClaimPartnerSearchTermUpdate = new Subject<string>();
  newClaimSearchedPartners: any[] = [];
  newClaimShowPartnerResults: boolean = false;
  newClaimLoadingPartners: boolean = false;
  newClaimSelectedPartner: any = null; // The partner selected for creating a new claim

  // View mode (for viewing existing claims) - SEPARATE from New Claim data
  viewMode: boolean = false; // false = create mode, true = view mode
  selectedClaim: any = null; // The claim being viewed
  viewClaimProducts: ClaimProduct[] = []; // Products for VIEW MODE ONLY
  viewClaimTitle: string = ''; // Title for VIEW MODE ONLY
  viewSelectedInvoice: any = null; // Invoice for VIEW MODE ONLY
  claimComments: any[] = []; // Comments for the selected claim
  newComment: string = ''; // New comment text
  newCommentFile: File | null = null; // Optional file for new comment
  newCommentFileName: string = ''; // File name for display
  submittingComment: boolean = false;
  loadingClaimDetails: boolean = false;

  // Reply functionality
  replyingToCommentID: number | null = null; // ID of comment being replied to
  newReply: string = ''; // New reply text
  newReplyFile: File | null = null; // Optional file for new reply
  newReplyFileName: string = ''; // File name for display
  submittingReply: boolean = false;

  // Collapsible sections for view mode
  viewProductsSectionExpanded: boolean = true; // View mode products section expanded by default

  // Solution management properties
  productSolutions: Map<number, ClaimProductSolution> = new Map(); // Map of ClaimProductID -> Solution
  savingSolution: Map<number, boolean> = new Map(); // Track saving state per product
  updatingClaimStatus: boolean = false;
  showClaimStatusDropdown: boolean = false; // For claim status dropdown in view mode
  showClaimStatusFilterDropdown: boolean = false; // For claim status filter dropdown in My Claims

  // Master data properties (loaded from GetOrderConfirmationMasterData)
  claimStatuses: any[] = []; // Claim status options for dropdown
  claimGeneralTypes: any[] = []; // General claim types
  // Note: productClaimTypes is already defined at line 84 and loaded per invoice

  viewCommentsSectionExpanded: boolean = true; // View mode comments section expanded by default

  // Partner Agents for Claim (View Mode)
  claimPartnerAgents: any[] = []; // All partner agents for the claim's partner
  loadingPartnerAgents: boolean = false; // Loading state for partner agents
  togglingAgentId: number | null = null; // Track which agent is being toggled (for loading indicator)
  viewAgentsSectionExpanded: boolean = true; // Agents section expanded by default

  // Partner Agents for New Claim (Create Mode)
  newClaimPartnerAgents: any[] = []; // All partner agents for the new claim's partner
  newClaimLoadingPartnerAgents: boolean = false; // Loading state for new claim partner agents
  newClaimAgentsSectionExpanded: boolean = true; // Agents section expanded by default

  constructor(
    public parent: ManagementComponent,
    private B2BService: B2BService,
    private sharedService: SharedService,
    private managementService: ManagementServiceService,
    private route: ActivatedRoute
  ) {
    // Debounce invoice search
    this.invoiceSearchTermUpdate.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(value => {
      if (value?.length >= 2) {
        this.searchInvoices(value);
      } else {
        this.searchedInvoices = [];
        this.showInvoiceResults = false;
      }
    });

    // Debounce partner search (My Claims tab)
    this.partnerSearchTermUpdate.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(value => {
      if (value?.length >= 2) {
        this.searchPartners(value);
      } else {
        this.searchedPartners = [];
        this.showPartnerResults = false;
      }
    });

    // Debounce partner search (New Claim tab)
    this.newClaimPartnerSearchTermUpdate.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(value => {
      if (value?.length >= 2) {
        this.searchNewClaimPartners(value);
      } else {
        this.newClaimSearchedPartners = [];
        this.newClaimShowPartnerResults = false;
      }
    });

    // Debounce reception search (External claims)
    this.receptionSearchTermUpdate.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(value => {
      if (value?.length >= 2) {
        this.searchReceptions(value);
      } else {
        this.searchedReceptions = [];
        this.showReceptionResults = false;
      }
    });
  }

  ngOnInit(): void {
    this.InitializeFilter();
    this.loadMasterData(); // Load master data first
  }

  // Load master data for claims (statuses, types, etc.)
  loadMasterData(): void {
    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage()
    };

    this.parent.loading = true;

    this.managementService.GetOrderConfirmationMasterData(request).subscribe(
      (response: any) => {
        this.parent.loading = false; // ✅ Always clear loading state

        if (response.Status === 1) {
          // Load the three master data lists
          this.claimStatuses = response.GetClaimStatusues || []; // Note: typo in backend model "Statusues"
          this.claimGeneralTypes = response.ClaimGeneralTypes || [];
          // productClaimTypes can also be loaded here, but it's also loaded per invoice
          if (response.ProductClaimTypes) {
            this.productClaimTypes = response.ProductClaimTypes;
          }

          // Set default claim general type to External (ID = 1)
          this.selectedClaimGeneralType = this.claimGeneralTypes.find(t => t.ID === 1) || null;

          console.log('✅ Claims Master Data loaded:', {
            claimStatuses: this.claimStatuses,
            claimGeneralTypes: this.claimGeneralTypes,
            productClaimTypes: this.productClaimTypes,
            defaultClaimGeneralType: this.selectedClaimGeneralType
          });

          // After master data is loaded, check URL params or load claims list
          this.processQueryParams();
        } else {
          this.parent.openSnackBar(response.Message || 'Failed to load master data', 'OK', 'alert-info-snackbar');
        }
      },
      (error) => {
        this.parent.loading = false; // ✅ Clear loading state on error
        console.error('Error loading master data:', error);
        this.parent.openSnackBar('Failed to load master data', 'OK', 'alert-info-snackbar');
      }
    );
  }

  // Initialize filter for My Claims
  InitializeFilter(): void {
    this.Filter = {};
    this.Filter.Pager = {};
    this.Filter.Pager.PageIndex = 1;
    this.Filter.Pager.PageSize = 50;
    this.Filter.Pager.TotalCount = 0;
    this.Filter.Partner = null; // null = all partners (for management view)
    this.Filter.ClaimGeneralType = null; // null = all types, -1 = invoice claims, 1 = reception claims
    this.Filter.ClaimStatus = null; // null = all statuses
  }

  // Process URL query params to auto-open a specific claim or new claim form
  processQueryParams(): void {
    this.route.queryParams.subscribe(params => {
      const claimId = params['id'] || null;
      const docId = params['docid'] || null;
      const dir = params['dir'] || null;

      if (claimId) {
        // Open existing claim in view mode by ID
        const parsedClaimId = parseInt(claimId, 10);
        if (!isNaN(parsedClaimId) && parsedClaimId > 0) {
          this.viewMode = true;
          this.showNewClaimTab = false;
          this.loadClaimDetails(parsedClaimId);
        } else {
          this.GetMyClaimsFiltered();
        }
      } else if (docId && dir) {
        // Open new claim tab pre-linked to a reception (dir=-1)
        const parsedDir = parseInt(dir, 10);
        this.showNewClaimTab = true;
        this.viewMode = false;

        if (parsedDir === -1) {
          // External (reception) claim type
          this.selectedClaimGeneralType = this.claimGeneralTypes.find(t => t.ID === 1) || null;
          // Pre-load the reception
          this.preloadReceptionForClaim(docId);
        }
      } else {
        // No query params — default: load claims list
        this.GetMyClaimsFiltered();
      }
    });
  }

  // Pre-load a reception by ID for creating a new claim from the receptions page
  preloadReceptionForClaim(receptionId: string): void {
    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      ReceptionID: receptionId
    };

    this.loadingProducts = true;

    this.managementService.GetReceptionProductsForClaim(request).subscribe(
      (response: any) => {
        this.loadingProducts = false;
        if (response.Status === 1) {
          this.claimProducts = response.ClaimProducts || [];
          this.productClaimTypes = response.ProductClaimTypes || [];

          // Initialize Proofs array for each product if not present
          this.claimProducts.forEach(product => {
            if (!product.Proofs) {
              product.Proofs = [];
            }
          });

          // Set the reception search term to indicate it's pre-loaded
          if (response.Reception) {
            this.selectedReception = response.Reception;
            this.receptionSearchTerm = response.Reception.AccountingNumber?.DisplayValue || response.Reception.Reference || '';
          }
        } else {
          this.parent.openSnackBar(response.Message || 'Failed to load reception products', 'OK', 'alert-info-snackbar');
        }
      },
      (error) => {
        this.loadingProducts = false;
        console.error('Error pre-loading reception for claim:', error);
        this.parent.openSnackBar('Failed to load reception products', 'OK', 'alert-info-snackbar');
      }
    );
  }

  // Get filtered claims list
  GetMyClaimsFiltered(page = 1): void {
    this.loadingClaims = true;
    this.Filter.Pager.PageIndex = page;

    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      Filter: this.Filter
    };

    this.B2BService.GetMyClaimsFiltered(request).subscribe(
      (response: any) => {
        this.loadingClaims = false;
        if (response.Status === 1) {
          this.claims = response.Claims || [];
          this.Filter.Pager = response.Pager;
        } else {
          this.parent.openSnackBar(response.Message, 'OK', 'alert-info-snackbar');
        }
      },
      (error) => {
        this.loadingClaims = false;
        this.parent.openSnackBar('Failed to load claims', 'OK', 'alert-info-snackbar');
      }
    );
  }

  // Tab switching methods
  showMyClaimsTab(): void {
    this.showNewClaimTab = false;
    this.viewMode = false;
    this.selectedClaim = null;
    this.GetMyClaimsFiltered();
  }

  showNewClaimTabClick(): void {
    this.showNewClaimTab = true;
    this.viewMode = false;
    this.selectedClaim = null;
  }

  // Open claim details in view mode
  openClaimDetails(claim: any): void {
    this.viewMode = true;
    this.showNewClaimTab = false;
    this.selectedClaim = claim;
    this.loadClaimDetails(claim.ID);
  }

  // Load claim details by ID
  loadClaimDetails(claimID: number): void {
    this.loadingClaimDetails = true;

    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      ClaimID: claimID
    };

    this.B2BService.GetClaimByID(request).subscribe(
      (response: any) => {
        this.loadingClaimDetails = false;
        if (response.Status === 1) {
          // DEBUG: Log the full claim object from backend
          console.log('🔍 DEBUG GetClaimByID - Full response.Claim:', response.Claim);
          console.log('🔍 DEBUG GetClaimByID - response.Claim.ID:', response.Claim.ID);
          console.log('🔍 DEBUG GetClaimByID - response.Claim.DBID:', response.Claim.DBID);
          console.log('🔍 DEBUG GetClaimByID - Object.keys:', Object.keys(response.Claim));

          this.selectedClaim = response.Claim;
          // Use SEPARATE view-mode objects (don't overwrite New Claim data)
          this.viewClaimProducts = response.Claim.ClaimedProducts || [];
          this.claimComments = response.Claim.Comments || [];
          this.viewClaimTitle = response.Claim.Title;
          this.viewSelectedInvoice = { Reference: response.Claim.DebatorPO };

          // Initialize Solution objects for each product
          this.viewClaimProducts.forEach(product => {
            if (!product.Solution) {
              product.Solution = { ID: 0, FKClaimProductID: product.DBID, Description: '' };
            }
          });

          // Load solutions for all products
          this.loadProductSolutions();

          // Load partner agents for this claim
          this.loadClaimPartnerAgents();
        } else {
          this.parent.openSnackBar(response.Message || 'Failed to load claim details', 'OK', 'alert-info-snackbar');
        }
      },
      (error) => {
        this.loadingClaimDetails = false;
        this.parent.openSnackBar('Failed to load claim details', 'OK', 'alert-info-snackbar');
        console.error('Error loading claim details:', error);
      }
    );
  }

  // Back to claims list from view mode
  backToClaimsList(): void {
    this.viewMode = false;
    this.selectedClaim = null;
    // Clear view-mode specific data
    this.viewClaimProducts = [];
    this.viewClaimTitle = '';
    this.viewSelectedInvoice = null;
    this.claimComments = [];
    this.newComment = '';
    this.newCommentFile = null;
    this.newCommentFileName = '';
    // Clear solution data
    this.productSolutions.clear();
    this.savingSolution.clear();
    // Clear partner agents data
    this.claimPartnerAgents = [];
    this.loadingPartnerAgents = false;
    this.togglingAgentId = null;
    this.GetMyClaimsFiltered();
  }

  // Solution Management Methods

  // Load solutions for all products in the claim
  loadProductSolutions(): void {
    if (!this.viewClaimProducts || this.viewClaimProducts.length === 0) {
      return;
    }

    this.viewClaimProducts.forEach(product => {
      // If solution already loaded from GetClaimByID, use it directly
      if (product.Solution && product.Solution.Description) {
        this.productSolutions.set(product.DBID, product.Solution);
      } else {
        // Initialize empty solution for the product if not exists
        if (!product.Solution) {
          product.Solution = { DBID: 0, FKClaimProductID: product.DBID, Description: '' };
        }
      }
    });
  }

  // Save or update a product solution
  saveProductSolution(product: ClaimProduct): void {
    // Check if claim is closed (adjust status value based on your system)
    if (this.selectedClaim && this.selectedClaim.Status === 3) {
      this.parent.openSnackBar('Cannot edit solutions for closed claims', 'OK', 'alert-info-snackbar');
      return;
    }

    const solutionData = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      ClaimProductID: product.DBID,
      Description: product.Solution?.Description || ''
    };

    // Validate description
    if (!solutionData.Description || solutionData.Description.trim() === '') {
      this.parent.openSnackBar('Please enter a solution description', 'OK', 'alert-info-snackbar');
      return;
    }

    this.savingSolution.set(product.DBID, true);

    this.B2BService.AddUpdateProductSolution(solutionData).subscribe(
      (data: any) => {
        this.savingSolution.set(product.DBID, false);

        if (data.Status === 1) {
          this.productSolutions.set(product.DBID, data.Data);
          product.Solution = data.Data;
          this.parent.openSnackBar(data.Message || 'Solution saved successfully', 'OK', 'light-info-snackbar');
        } else {
          this.parent.openSnackBar(data.Message || 'Failed to save solution', 'OK', 'alert-info-snackbar');
        }
      },
      error => {
        this.savingSolution.set(product.DBID, false);
        this.parent.openSnackBar('Error saving solution', 'OK', 'alert-info-snackbar');
        console.error('Error saving solution:', error);
      }
    );
  }

  // Check if a product has a solution
  hasProductSolution(product: ClaimProduct): boolean {
    const solution = this.productSolutions.get(product.DBID);
    return solution != null && solution.Description != null && solution.Description.trim().length > 0;
  }

  // Check if all products have solutions
  allProductsHaveSolutions(): boolean {
    if (!this.viewClaimProducts || this.viewClaimProducts.length === 0) {
      return false;
    }

    return this.viewClaimProducts.every(product => this.hasProductSolution(product));
  }

  // Get status label by ID
  getStatusLabel(statusValue: number): string {
    const status = this.claimStatuses.find(s => s.ID === statusValue);
    return status ? status.Name : '';
  }

  // Get claim general type label by ID
  getClaimGeneralTypeLabel(typeID: number): string {
    const type = this.claimGeneralTypes.find(t => t.ID === typeID);
    return type ? type.Name : '';
  }

  // Select claim status from dropdown
  selectClaimStatus(status: any): void {
    if (!this.selectedClaim) {
      return;
    }

    // Block status change if not all products have solutions
    if (!this.allProductsHaveSolutions()) {
      this.parent.openSnackBar('Cannot change status: All products must have solutions first', 'OK', 'alert-info-snackbar');
      this.showClaimStatusDropdown = false;
      return;
    }

    // Update the Status object (not just ID)
    this.selectedClaim.Status = {
      ID: status.ID,
      Name: status.Name,
      Description: status.Description || ''
    };

    this.showClaimStatusDropdown = false;
    this.updateClaimStatus(status.ID);
  }

  // Update claim status
  updateClaimStatus(newStatus: number): void {
    if (!this.selectedClaim) {
      return;
    }

    // Block ANY status change if not all products have solutions
    if (!this.allProductsHaveSolutions()) {
      this.parent.openSnackBar('Cannot change status: All products must have solutions first', 'OK', 'alert-info-snackbar');
      return;
    }

    // DEBUG: Log selectedClaim to see all available properties
    console.log('🔍 DEBUG updateClaimStatus - selectedClaim object:', this.selectedClaim);
    console.log('🔍 DEBUG updateClaimStatus - selectedClaim.ID:', this.selectedClaim.ID);
    console.log('🔍 DEBUG updateClaimStatus - selectedClaim.DBID:', this.selectedClaim.DBID);
    console.log('🔍 DEBUG updateClaimStatus - Object.keys:', Object.keys(this.selectedClaim));

    this.updatingClaimStatus = true;

    const requestPayload = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      ClaimID: this.selectedClaim.ID,
      Status: newStatus
    };
    console.log('🔍 DEBUG updateClaimStatus - Request payload:', requestPayload);

    this.B2BService.UpdateClaimStatus(requestPayload).subscribe(
      (data: any) => {
        this.updatingClaimStatus = false;

        if (data.Status === 1) {
          // Status already updated in selectClaimStatus, just show message
          this.parent.openSnackBar(data.Message || 'Claim status updated successfully', 'OK', 'light-info-snackbar');

          // Reload claims list to reflect the change
          this.GetMyClaimsFiltered();
        } else {
          this.parent.openSnackBar(data.Message || 'Failed to update claim status', 'OK', 'alert-info-snackbar');
        }
      },
      error => {
        this.updatingClaimStatus = false;
        this.parent.openSnackBar('Error updating claim status', 'OK', 'alert-info-snackbar');
        console.error('Error updating claim status:', error);
      }
    );
  }

  // Partner filter methods
  searchPartners(searchTerm: string): void {
    this.loadingPartners = true;
    this.showPartnerResults = true;

    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      SearchTerm: searchTerm,
      PageIndex: 1,
      PageSize: 20,
      CompanyID: this.parent.Company?.ID
    };

    this.sharedService.GetPartnersFiltered(request).subscribe(
      (response: any) => {
        if (response.Status === 1) {
          this.searchedPartners = response.Partners || [];
        } else {
          this.parent.openSnackBar(response.Message, 'OK', 'alert-info-snackbar');
          this.searchedPartners = [];
        }
        this.loadingPartners = false;
      },
      () => {
        this.loadingPartners = false;
        this.searchedPartners = [];
      }
    );
  }

  selectPartner(partner: any): void {
    this.Filter.Partner = partner;
    this.partnerSearchTerm = partner.Name;
    this.showPartnerResults = false;
    this.GetMyClaimsFiltered(1); // Reload claims with partner filter
  }

  clearPartnerFilter(): void {
    this.Filter.Partner = null;
    this.partnerSearchTerm = '';
    this.searchedPartners = [];
    this.showPartnerResults = false;
    this.GetMyClaimsFiltered(1); // Reload claims without partner filter
  }

  // New Claim - Claim Type Selection Methods
  selectClaimGeneralType(type: any): void {
    // If switching claim type, clear the document selection
    if (this.selectedClaimGeneralType?.ID !== type?.ID) {
      // Clear invoice search state
      this.invoiceSearchTerm = '';
      this.selectedInvoice = null;
      this.searchedInvoices = [];
      this.showInvoiceResults = false;
      // Clear reception search state
      this.receptionSearchTerm = '';
      this.selectedReception = null;
      this.searchedReceptions = [];
      this.showReceptionResults = false;
      // Clear products
      this.claimProducts = [];
      this.productClaimTypes = [];
    }
    this.selectedClaimGeneralType = type;
    console.log('✅ Selected Claim General Type:', type);
  }

  // New Claim partner selection methods
  searchNewClaimPartners(searchTerm: string): void {
    this.newClaimLoadingPartners = true;
    this.newClaimShowPartnerResults = true;

    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      SearchTerm: searchTerm,
      PageIndex: 1,
      PageSize: 20,
      CompanyID: this.parent.Company?.ID
    };

    this.sharedService.GetPartnersFiltered(request).subscribe(
      (response: any) => {
        if (response.Status === 1) {
          this.newClaimSearchedPartners = response.Partners || [];
        } else {
          this.parent.openSnackBar(response.Message, 'OK', 'alert-info-snackbar');
          this.newClaimSearchedPartners = [];
        }
        this.newClaimLoadingPartners = false;
      },
      () => {
        this.newClaimLoadingPartners = false;
        this.newClaimSearchedPartners = [];
      }
    );
  }

  selectNewClaimPartner(partner: any): void {
    this.newClaimSelectedPartner = partner;
    this.newClaimPartnerSearchTerm = partner.Name;
    this.newClaimShowPartnerResults = false;
    // Load partner agents for the selected partner
    this.loadNewClaimPartnerAgents();
  }

  clearNewClaimPartner(): void {
    this.newClaimSelectedPartner = null;
    this.newClaimPartnerSearchTerm = '';
    this.newClaimSearchedPartners = [];
    this.newClaimShowPartnerResults = false;
    // Clear partner agents when partner is cleared
    this.newClaimPartnerAgents = [];
  }

  // Load partner agents for new claim
  loadNewClaimPartnerAgents(): void {
    if (!this.newClaimSelectedPartner?.ID) {
      this.newClaimPartnerAgents = [];
      return;
    }

    this.newClaimLoadingPartnerAgents = true;

    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      PartnerID: this.newClaimSelectedPartner.ID
    };

    this.sharedService.GetPartnerAgents(request).subscribe(
      (response: any) => {
        this.newClaimLoadingPartnerAgents = false;
        if (response.Status === 1) {
          this.newClaimPartnerAgents = response.PartnerAgents || [];
          // Mark all as unselected initially
          this.newClaimPartnerAgents.forEach(agent => agent.IsSelected = false);
        } else {
          this.newClaimPartnerAgents = [];
          this.parent.openSnackBar(response.Message || 'Failed to load partner agents', 'OK', 'alert-info-snackbar');
        }
      },
      () => {
        this.newClaimLoadingPartnerAgents = false;
        this.newClaimPartnerAgents = [];
        this.parent.openSnackBar('Failed to load partner agents', 'OK', 'alert-info-snackbar');
      }
    );
  }

  // Toggle partner agent selection for new claim (local only, not saved to DB yet)
  toggleNewClaimPartnerAgent(agent: any): void {
    agent.IsSelected = !agent.IsSelected;
  }

  // Toggle new claim agents section expand/collapse
  toggleNewClaimAgentsSection(): void {
    this.newClaimAgentsSectionExpanded = !this.newClaimAgentsSectionExpanded;
  }

  // Get count of selected partner agents for new claim
  getNewClaimSelectedAgentsCount(): number {
    return this.newClaimPartnerAgents.filter(a => a.IsSelected).length;
  }

  // Handle comment file selection
  onCommentFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      const validVideoTypes = ['video/mp4'];
      const validTypes = [...validImageTypes, ...validVideoTypes];

      if (!validTypes.includes(file.type)) {
        this.parent.openSnackBar('Invalid file type. Only JPEG, PNG and MP4 files are allowed.', 'OK', 'alert-info-snackbar');
        event.target.value = '';
        return;
      }

      // Validate file size - 10MB for images, 50MB for videos
      const isVideo = validVideoTypes.includes(file.type);
      const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
      const maxSizeLabel = isVideo ? '50MB' : '10MB';

      if (file.size > maxSize) {
        this.parent.openSnackBar(`File size must be less than ${maxSizeLabel}.`, 'OK', 'alert-info-snackbar');
        event.target.value = '';
        return;
      }

      this.newCommentFile = file;
      this.newCommentFileName = file.name;
    }
  }

  // Remove selected comment file
  removeCommentFile(): void {
    this.newCommentFile = null;
    this.newCommentFileName = '';
  }

  // Add comment to claim
  addComment(): void {
    if (!this.newComment.trim()) {
      this.parent.openSnackBar('Please enter a comment', 'OK', 'alert-info-snackbar');
      return;
    }

    this.submittingComment = true;

    // Convert file to Base64 if present
    if (this.newCommentFile) {
      const reader = new FileReader();
      reader.onload = () => {
        const base64File = (reader.result as string).split(',')[1];
        this.submitComment(base64File);
      };
      reader.readAsDataURL(this.newCommentFile);
    } else {
      this.submitComment('');
    }
  }

  // Download proof attachment
  downloadProof(proof: any, index: number): void {
    if (!proof.Base64UploadedDocument) {
      this.parent.openSnackBar('No file available for download', 'OK', 'alert-info-snackbar');
      return;
    }

    try {
      // Determine file extension from Base64 header or default to jpg
      const base64Data = proof.Base64UploadedDocument;
      let mimeType = 'image/jpeg';
      let extension = 'jpg';

      // Try to detect file type from Base64 data
      if (base64Data.startsWith('/9j/')) {
        mimeType = 'image/jpeg';
        extension = 'jpg';
      } else if (base64Data.startsWith('iVBORw0KGgo')) {
        mimeType = 'image/png';
        extension = 'png';
      } else if (base64Data.startsWith('AAAA')) {
        mimeType = 'video/mp4';
        extension = 'mp4';
      }

      // Convert Base64 to Blob
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `proof_${index + 1}.${extension}`;
      link.click();

      // Clean up
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading proof:', error);
      this.parent.openSnackBar('Failed to download file', 'OK', 'alert-info-snackbar');
    }
  }

  // Download comment attachment
  downloadCommentAttachment(comment: any): void {
    if (!comment.Base64UploadedDocument) {
      this.parent.openSnackBar('No file available for download', 'OK', 'alert-info-snackbar');
      return;
    }

    try {
      // Determine file extension from Base64 header or default to jpg
      const base64Data = comment.Base64UploadedDocument;
      let mimeType = 'image/jpeg';
      let extension = 'jpg';

      // Try to detect file type from Base64 data
      if (base64Data.startsWith('/9j/')) {
        mimeType = 'image/jpeg';
        extension = 'jpg';
      } else if (base64Data.startsWith('iVBORw0KGgo')) {
        mimeType = 'image/png';
        extension = 'png';
      } else if (base64Data.startsWith('AAAA')) {
        mimeType = 'video/mp4';
        extension = 'mp4';
      }

      // Convert Base64 to Blob
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `comment_attachment_${comment.ID}.${extension}`;
      link.click();

      // Clean up
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      this.parent.openSnackBar('Failed to download file', 'OK', 'alert-info-snackbar');
    }
  }

  // Submit comment to backend
  submitComment(base64File: string): void {
    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      ClaimComment: {
        FKClaimID: this.selectedClaim.ID,
        Comment: this.newComment,
        Base64UploadedDocument: base64File
      }
    };

    this.B2BService.AddClaimComment(request).subscribe(
      (response: any) => {
        this.submittingComment = false;
        if (response.Status === 1) {
          // Add the new comment to the list
          this.claimComments.push(response.ClaimComment);
          // Reset form
          this.newComment = '';
          this.newCommentFile = null;
          this.newCommentFileName = '';
          this.parent.openSnackBar('Comment added successfully', 'OK', 'light-info-snackbar');
        } else {
          this.parent.openSnackBar(response.Message || 'Failed to add comment', 'OK', 'alert-info-snackbar');
        }
      },
      (error) => {
        this.submittingComment = false;
        this.parent.openSnackBar('Failed to add comment', 'OK', 'alert-info-snackbar');
        console.error('Error adding comment:', error);
      }
    );
  }

  // Start replying to a comment
  startReply(comment: any): void {
    this.replyingToCommentID = comment.ID;
    this.newReply = '';
    this.newReplyFile = null;
    this.newReplyFileName = '';
  }

  // Cancel reply
  cancelReply(): void {
    this.replyingToCommentID = null;
    this.newReply = '';
    this.newReplyFile = null;
    this.newReplyFileName = '';
  }

  // Handle reply file selection
  onReplyFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      const validVideoTypes = ['video/mp4'];
      const validTypes = [...validImageTypes, ...validVideoTypes];

      if (!validTypes.includes(file.type)) {
        this.parent.openSnackBar('Invalid file type. Only JPEG, PNG and MP4 files are allowed.', 'OK', 'alert-info-snackbar');
        event.target.value = '';
        return;
      }

      // Validate file size - 10MB for images, 50MB for videos
      const isVideo = validVideoTypes.includes(file.type);
      const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
      const maxSizeLabel = isVideo ? '50MB' : '10MB';

      if (file.size > maxSize) {
        this.parent.openSnackBar(`File size must be less than ${maxSizeLabel}.`, 'OK', 'alert-info-snackbar');
        event.target.value = '';
        return;
      }

      this.newReplyFile = file;
      this.newReplyFileName = file.name;
    }
  }

  // Remove selected reply file
  removeReplyFile(): void {
    this.newReplyFile = null;
    this.newReplyFileName = '';
  }

  // Add reply (convert file to Base64 and submit)
  addReply(): void {
    if (!this.newReply || !this.replyingToCommentID) {
      return;
    }

    this.submittingReply = true;

    // Convert file to Base64 if present
    if (this.newReplyFile) {
      const reader = new FileReader();
      reader.onload = () => {
        const base64File = (reader.result as string).split(',')[1];
        this.submitReply(base64File);
      };
      reader.readAsDataURL(this.newReplyFile);
    } else {
      this.submitReply('');
    }
  }

  // Submit reply to backend
  submitReply(base64File: string): void {
    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      ClaimCommentReply: {
        FKClaimCommentID: this.replyingToCommentID,
        Reply: this.newReply,
        Base64UploadedDocument: base64File
      }
    };

    this.B2BService.AddClaimCommentReply(request).subscribe(
      (response: any) => {
        this.submittingReply = false;
        if (response.Status === 1) {
          // Find the parent comment and add the reply to it
          const parentComment = this.claimComments.find(c => c.ID === this.replyingToCommentID);
          if (parentComment) {
            if (!parentComment.Replies) {
              parentComment.Replies = [];
            }
            parentComment.Replies.push(response.ClaimCommentReply);
          }
          // Reset reply form
          this.cancelReply();
          this.parent.openSnackBar('Reply added successfully', 'OK', 'light-info-snackbar');
        } else {
          this.parent.openSnackBar(response.Message || 'Failed to add reply', 'OK', 'alert-info-snackbar');
        }
      },
      (error) => {
        this.submittingReply = false;
        this.parent.openSnackBar('Failed to add reply', 'OK', 'alert-info-snackbar');
        console.error('Error adding reply:', error);
      }
    );
  }

  // Download reply attachment
  downloadReplyAttachment(reply: any): void {
    if (!reply.Base64UploadedDocument) {
      this.parent.openSnackBar('No file available for download', 'OK', 'alert-info-snackbar');
      return;
    }

    try {
      // Detect file type from Base64 header
      let mimeType = 'application/octet-stream';
      let extension = 'bin';

      if (reply.Base64UploadedDocument.startsWith('/9j/')) {
        mimeType = 'image/jpeg';
        extension = 'jpg';
      } else if (reply.Base64UploadedDocument.startsWith('iVBORw0KGgo')) {
        mimeType = 'image/png';
        extension = 'png';
      } else if (reply.Base64UploadedDocument.startsWith('AAAA')) {
        mimeType = 'video/mp4';
        extension = 'mp4';
      }

      // Convert Base64 to Blob
      const byteCharacters = atob(reply.Base64UploadedDocument);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `reply_${reply.ID}.${extension}`;
      link.click();

      // Clean up
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading reply attachment:', error);
      this.parent.openSnackBar('Failed to download file', 'OK', 'alert-info-snackbar');
    }
  }

  // Toggle collapsible sections for view mode
  toggleViewProductsSection(): void {
    this.viewProductsSectionExpanded = !this.viewProductsSectionExpanded;
  }

  toggleViewCommentsSection(): void {
    this.viewCommentsSectionExpanded = !this.viewCommentsSectionExpanded;
  }

  // Toggle agents section visibility
  toggleViewAgentsSection(): void {
    this.viewAgentsSectionExpanded = !this.viewAgentsSectionExpanded;
  }

  // Load all partner agents for the claim's partner
  loadClaimPartnerAgents(): void {
    if (!this.selectedClaim?.Partner?.ID) {
      return;
    }

    this.loadingPartnerAgents = true;

    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      PartnerID: this.selectedClaim.Partner.ID
    };

    this.sharedService.GetPartnerAgents(request).subscribe(
      (response: any) => {
        if (response.Status === 1) {
          this.claimPartnerAgents = response.PartnerAgents || [];
          // Now load claim-specific agents to set IsSelected
          this.loadClaimSpecificPartnerAgents();
        } else {
          this.loadingPartnerAgents = false;
          this.parent.openSnackBar(response.Message || 'Failed to load partner agents', 'OK', 'alert-info-snackbar');
        }
      },
      (error) => {
        this.loadingPartnerAgents = false;
        this.parent.openSnackBar('Failed to load partner agents', 'OK', 'alert-info-snackbar');
      }
    );
  }

  // Load claim-specific partner agents and mark them as selected
  loadClaimSpecificPartnerAgents(): void {
    if (!this.selectedClaim?.ID) {
      this.loadingPartnerAgents = false;
      return;
    }

    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      ClaimID: this.selectedClaim.ID
    };

    this.B2BService.GetClaimPartnerAgents(request).subscribe(
      (response: any) => {
        this.loadingPartnerAgents = false;
        if (response.Status === 1 && response.PartnerAgents) {
          // Mark agents as selected if they exist in claim-specific list
          const claimAgentIds = response.PartnerAgents.map((a: any) => a.ID);
          this.claimPartnerAgents.forEach(agent => {
            agent.IsSelected = claimAgentIds.includes(agent.ID);
          });
        }
      },
      (error) => {
        this.loadingPartnerAgents = false;
        this.parent.openSnackBar('Failed to load claim-specific agents', 'OK', 'alert-info-snackbar');
      }
    );
  }

  // Toggle agent selection (add or remove from claim)
  toggleClaimPartnerAgent(agent: any): void {
    if (!this.selectedClaim?.ID || this.togglingAgentId !== null) {
      return;
    }

    this.togglingAgentId = agent.ID;
    const wasSelected = agent.IsSelected;

    // Optimistically update UI
    agent.IsSelected = !agent.IsSelected;

    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      PartnerAgentID: agent.ID,
      ClaimID: this.selectedClaim.ID
    };

    const serviceCall = wasSelected
      ? this.B2BService.DeleteClaimPartnerAgent(request)
      : this.B2BService.AddClaimPartnerAgent(request);

    serviceCall.subscribe(
      (response: any) => {
        this.togglingAgentId = null;
        if (response.Status === 1) {
          this.parent.openSnackBar(
            wasSelected ? 'Agent removed from claim' : 'Agent added to claim',
            'OK',
            'light-info-snackbar'
          );
        } else {
          // Revert on error
          agent.IsSelected = wasSelected;
          this.parent.openSnackBar(response.Message || 'Failed to update agent', 'OK', 'alert-info-snackbar');
        }
      },
      (error) => {
        this.togglingAgentId = null;
        // Revert on error
        agent.IsSelected = wasSelected;
        this.parent.openSnackBar('Failed to update agent', 'OK', 'alert-info-snackbar');
      }
    );
  }

  // Invoice search methods
  searchInvoices(searchTerm: string): void {
    this.loadingInvoices = true;
    this.showInvoiceResults = true;

    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      Filter: {
        Product: searchTerm,
        Partner: this.newClaimSelectedPartner, // Filter by selected partner (customer)
        Pager: {
          PageIndex: 1,
          PageSize: 20,
          TotalCount: 0
        },
        Company: this.parent.Company,
      },
    };

    this.B2BService.GetB2BInvoices(request).subscribe(
      (response: any) => {
        if (response.Status === 1) {
          this.searchedInvoices = response.Invoices || [];
        } else {
          this.parent.openSnackBar(response.Message, 'OK', 'alert-info-snackbar');
          this.searchedInvoices = [];
        }
        this.loadingInvoices = false;
      },
      (error) => {
        console.error('Error searching invoices:', error);
        this.loadingInvoices = false;
        this.searchedInvoices = [];
      }
    );
  }

  selectInvoice(invoice: any): void {
    this.selectedInvoice = invoice;
    this.invoiceSearchTerm = invoice.Reference || invoice.Number || '';
    this.showInvoiceResults = false;
    this.searchedInvoices = [];

    // Load products for the selected invoice
    this.loadInvoiceProducts(invoice.ID);
  }

  loadInvoiceProducts(invoiceID: string): void {
    this.loadingProducts = true;
    this.claimProducts = [];
    this.productClaimTypes = [];

    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      InvoiceID: invoiceID
    };

    this.B2BService.GetInvoiceProductsForClaim(request).subscribe(
      (response: any) => {
        this.loadingProducts = false;
        if (response.Status === 1) {
          this.claimProducts = response.ClaimProducts || [];
          this.productClaimTypes = response.ProductClaimTypes || [];

          // Initialize Proofs array for each product if not present
          this.claimProducts.forEach(product => {
            if (!product.Proofs) {
              product.Proofs = [];
            }
          });
        } else {
          this.parent.openSnackBar(response.Message || 'Failed to load products', 'OK', 'alert-info-snackbar');
        }
      },
      (error) => {
        this.loadingProducts = false;
        console.error('Error loading invoice products:', error);
        this.parent.openSnackBar('Failed to load invoice products', 'OK', 'alert-info-snackbar');
      }
    );
  }

  clearInvoiceSearch(): void {
    this.invoiceSearchTerm = '';
    this.selectedInvoice = null;
    this.searchedInvoices = [];
    this.showInvoiceResults = false;
    this.claimProducts = [];
    this.productClaimTypes = [];
  }

  onInvoiceSearchFocus(): void {
    this.invoiceSearchHighlighted = true;
    if (this.invoiceSearchTerm?.length >= 2) {
      this.showInvoiceResults = true;
    }
  }

  onInvoiceSearchBlur(): void {
    this.invoiceSearchHighlighted = false;
  }

  // Focus invoice search input (called when Step 1 is clicked)
  focusInvoiceSearch(): void {
    if (this.invoiceSearchInput) {
      this.invoiceSearchInput.nativeElement.focus();
    }
  }

  // Reception search methods (for External claims)
  searchReceptions(searchTerm: string): void {
    this.loadingReceptions = true;
    this.showReceptionResults = true;

    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      SearchTerm: searchTerm,
      CompanyID: this.parent.Company?.ID,
      PartnerID: this.newClaimSelectedPartner?.ID, // Filter by selected partner (supplier)
      PageIndex: 1,
      PageSize: 20
    };

    this.managementService.SearchReceptionsByProductFTS(request).subscribe(
      (response: any) => {
        if (response.Status === 1) {
          this.searchedReceptions = response.Receptions || [];
        } else {
          this.parent.openSnackBar(response.Message, 'OK', 'alert-info-snackbar');
          this.searchedReceptions = [];
        }
        this.loadingReceptions = false;
      },
      (error: any) => {
        console.error('Error searching receptions:', error);
        this.loadingReceptions = false;
        this.searchedReceptions = [];
      }
    );
  }

  selectReception(reception: any): void {
    this.selectedReception = reception;
    this.receptionSearchTerm = reception.AccountingNumber?.DisplayValue || reception.Reference || '';
    this.showReceptionResults = false;
    this.searchedReceptions = [];

    // Load products for the selected reception
    this.loadReceptionProducts(reception.ID);
  }

  loadReceptionProducts(receptionID: string): void {
    this.loadingProducts = true;
    this.claimProducts = [];
    this.productClaimTypes = [];

    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      ReceptionID: receptionID
    };

    this.managementService.GetReceptionProductsForClaim(request).subscribe(
      (response: any) => {
        this.loadingProducts = false;
        if (response.Status === 1) {
          this.claimProducts = response.ClaimProducts || [];
          this.productClaimTypes = response.ProductClaimTypes || [];

          // Initialize Proofs array for each product if not present
          this.claimProducts.forEach(product => {
            if (!product.Proofs) {
              product.Proofs = [];
            }
          });
        } else {
          this.parent.openSnackBar(response.Message || 'Failed to load products', 'OK', 'alert-info-snackbar');
        }
      },
      (error) => {
        this.loadingProducts = false;
        console.error('Error loading reception products:', error);
        this.parent.openSnackBar('Failed to load reception products', 'OK', 'alert-info-snackbar');
      }
    );
  }

  clearReceptionSearch(): void {
    this.receptionSearchTerm = '';
    this.selectedReception = null;
    this.searchedReceptions = [];
    this.showReceptionResults = false;
    this.claimProducts = [];
    this.productClaimTypes = [];
  }

  onReceptionSearchFocus(): void {
    this.receptionSearchHighlighted = true;
    if (this.receptionSearchTerm?.length >= 2) {
      this.showReceptionResults = true;
    }
  }

  onReceptionSearchBlur(): void {
    this.receptionSearchHighlighted = false;
  }

  // Focus reception search input (called when Step 2 is clicked for External claims)
  focusReceptionSearch(): void {
    if (this.receptionSearchInput) {
      this.receptionSearchInput.nativeElement.focus();
    }
  }

  // Product-level file upload methods
  onProductFileSelected(event: any, product: ClaimProduct): void {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      const validVideoTypes = ['video/mp4'];
      const validTypes = [...validImageTypes, ...validVideoTypes];

      if (!validTypes.includes(file.type)) {
        this.parent.openSnackBar('Invalid file type. Only JPEG, PNG and MP4 files are allowed.', 'OK', 'alert-info-snackbar');
        event.target.value = '';
        return;
      }

      // Validate file size - 10MB for images, 50MB for videos
      const isVideo = validVideoTypes.includes(file.type);
      const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
      const maxSizeLabel = isVideo ? '50MB' : '10MB';

      if (file.size > maxSize) {
        this.parent.openSnackBar(`File size must be less than ${maxSizeLabel}.`, 'OK', 'alert-info-snackbar');
        event.target.value = '';
        return;
      }

      // Convert to Base64 and add to product's Proofs array
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        const proof: ProductClaimProof = {
          ID: 0,
          FKClaimProductID: product.DBID || 0,
          UploadedDocument: null,
          Base64UploadedDocument: base64, // ✅ Backend will convert this to byte[]
          FileName: file.name
        };
        product.Proofs.push(proof);
      };
      reader.readAsDataURL(file);
    }
    // Reset the input so the same file can be selected again
    event.target.value = '';
  }

  removeProductProof(product: ClaimProduct, proofIndex: number): void {
    product.Proofs.splice(proofIndex, 1);
  }

  // Only allow digits 0-9 in quantity input
  onQuantityKeyPress(event: KeyboardEvent): boolean {
    // Allow only digits 0-9
    if (!/^[0-9]$/.test(event.key)) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  // Handle paste - only allow numeric values
  onQuantityPaste(event: ClipboardEvent): void {
    const pastedData = event.clipboardData?.getData('text') || '';
    // If pasted data contains non-numeric characters, prevent paste
    if (!/^\d+$/.test(pastedData)) {
      event.preventDefault();
    }
  }

  // Quantity validation - ensure ClaimedQuantity doesn't exceed DocQuantity
  validateClaimedQuantity(product: ClaimProduct): void {
    // Convert to number in case it's a string
    let qty = parseInt(product.ClaimedQuantity as any, 10);
    if (isNaN(qty) || qty < 0) {
      qty = 0;
    }
    if (qty > product.DocQuantity) {
      qty = product.DocQuantity;
    }
    product.ClaimedQuantity = qty;
  }

  // Increment quantity
  incrementQuantity(product: ClaimProduct): void {
    let qty = parseInt(product.ClaimedQuantity as any, 10) || 0;
    if (qty < product.DocQuantity) {
      product.ClaimedQuantity = qty + 1;
    }
  }

  // Decrement quantity
  decrementQuantity(product: ClaimProduct): void {
    let qty = parseInt(product.ClaimedQuantity as any, 10) || 0;
    if (qty > 0) {
      product.ClaimedQuantity = qty - 1;
    }
  }

  // Check if any product has a claim (ClaimedQuantity > 0 and ClaimType selected)
  hasValidProductClaims(): boolean {
    return this.claimProducts.some(p => p.ClaimedQuantity > 0 && p.ClaimType != null);
  }

  // Toggle claim type dropdown for a specific product
  toggleClaimTypeDropdown(index: number): void {
    if (this.openClaimTypeDropdownIndex === index) {
      this.openClaimTypeDropdownIndex = -1;
    } else {
      this.openClaimTypeDropdownIndex = index;
    }
  }

  // Select claim type for a product
  selectClaimType(product: ClaimProduct, type: any): void {
    product.ClaimType = type;
    this.openClaimTypeDropdownIndex = -1;
  }

  // Close claim type dropdown
  closeClaimTypeDropdown(): void {
    this.openClaimTypeDropdownIndex = -1;
  }

  // Clear claim type selection
  clearClaimType(product: ClaimProduct, event: Event): void {
    event.stopPropagation(); // Prevent dropdown from opening
    product.ClaimType = null;
    product.UserClaimDescription = null;
    product.Proofs = [];
  }

  // Toggle products section expand/collapse
  toggleProductsSection(): void {
    this.productsExpanded = !this.productsExpanded;
  }

  // Form validation
  isFormValid(): boolean {
    const hasClaimType = this.selectedClaimGeneralType != null;
    const hasPartner = this.newClaimSelectedPartner != null;

    // For Internal claims (-1), require selectedInvoice
    // For External claims (1), require selectedReception
    const hasDocument = this.selectedClaimGeneralType?.ID === -1
      ? this.selectedInvoice != null
      : this.selectedClaimGeneralType?.ID === 1
        ? this.selectedReception != null
        : false;

    return hasClaimType && hasPartner && hasDocument;
  }

  // Validate products with claimed quantities
  validateClaimProducts(): ProductValidationError[] {
    const errors: ProductValidationError[] = [];

    this.claimProducts.forEach(product => {
      if (product.ClaimedQuantity > 0) {
        const error: ProductValidationError = {
          product: product,
          missingClaimType: !product.ClaimType,
          missingDescription: !product.UserClaimDescription || product.UserClaimDescription.trim().length === 0,
          missingProof: false // Proof upload is optional on this page
        };

        // Only add to errors if at least one field is missing (excluding proof)
        if (error.missingClaimType || error.missingDescription) {
          errors.push(error);
        }
      }
    });

    return errors;
  }

  // Dismiss product validation errors
  dismissProductValidationErrors(): void {
    this.showProductValidationErrors = false;
    this.productValidationErrors = [];
  }

  // Submit claim
  submitClaim(): void {
    if (!this.isFormValid()) {
      this.parent.openSnackBar('Please fill in all required fields.', 'OK', 'alert-info-snackbar');
      return;
    }

    // Validate products
    this.productValidationErrors = this.validateClaimProducts();
    if (this.productValidationErrors.length > 0) {
      this.showProductValidationErrors = true;
      // Scroll to the error message
      setTimeout(() => {
        const errorElement = document.querySelector('.ClaimProductValidationError');
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      return;
    }

    this.submitting = true;
    this.submitSuccess = false;
    this.submitError = '';
    this.showProductValidationErrors = false;

    // Filter products with ClaimedQuantity > 0
    const productsToSubmit = this.claimProducts.filter(p => p.ClaimedQuantity > 0);

    // Determine document reference based on claim type
    const isInternalClaim = this.selectedClaimGeneralType?.ID === -1;
    const documentReference = isInternalClaim
      ? (this.selectedInvoice?.Reference || this.selectedInvoice?.Number || '')
      : (this.selectedReception?.AccountingNumber?.DisplayValue || this.selectedReception?.Reference || '');

    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      Claim: {
        Title: this.claimTitle.trim(),
        DebatorPO: documentReference,
        Partner: this.newClaimSelectedPartner || (isInternalClaim ? this.selectedInvoice?.Partner : this.selectedReception?.Partner) || null,
        GeneralType: this.selectedClaimGeneralType, // Must match backend Claim.GeneralType property name
        ClaimedProducts: productsToSubmit,
        // Include InvoiceID or ReceptionID based on claim type
        InvoiceID: isInternalClaim ? this.selectedInvoice?.ID : null,
        ReceptionID: !isInternalClaim ? this.selectedReception?.ID : null
      },
      // Include selected partner agent IDs for the new claim
      PartnerAgentIDs: this.newClaimPartnerAgents
        .filter(agent => agent.IsSelected)
        .map(agent => agent.ID)
    };

    this.B2BService.AddNewClaim(request).subscribe(
      (response: any) => {
        this.submitting = false;
        if (response.Status === 1) {
          this.submitSuccess = true;
          this.parent.openSnackBar('Claim submitted successfully!', 'OK', 'light-info-snackbar');
          this.resetForm();
        } else {
          this.submitError = response.Message || 'Failed to submit claim.';
          this.parent.openSnackBar(this.submitError, 'OK', 'alert-info-snackbar');
        }
      },
      (error) => {
        this.submitting = false;
        this.submitError = 'Failed to connect to server.';
        this.parent.openSnackBar(this.submitError, 'OK', 'alert-info-snackbar');
        console.error('Error submitting claim:', error);
      }
    );
  }

  // Reset form
  resetForm(): void {
    // Clear invoice search state
    this.invoiceSearchTerm = '';
    this.selectedInvoice = null;
    this.searchedInvoices = [];
    this.showInvoiceResults = false;
    // Clear reception search state
    this.receptionSearchTerm = '';
    this.selectedReception = null;
    this.searchedReceptions = [];
    this.showReceptionResults = false;
    // Clear products
    this.claimProducts = [];
    this.productClaimTypes = [];
    this.claimTitle = '';
    this.submitSuccess = false;
    this.submitError = '';
    // Reset claim type selection to default (External, ID = 1)
    this.selectedClaimGeneralType = this.claimGeneralTypes.find(t => t.ID === 1) || null;
    // Clear new claim partner selection
    this.newClaimSelectedPartner = null;
    this.newClaimPartnerSearchTerm = '';
    this.newClaimSearchedPartners = [];
    this.newClaimShowPartnerResults = false;
    // Clear new claim partner agents
    this.newClaimPartnerAgents = [];
    this.newClaimAgentsSectionExpanded = true;
  }

  // Generate public URL for new claim and copy to clipboard
  generatePublicUrl(claim: any, event?: Event): void {
    // Stop event propagation to prevent opening claim details
    if (event) {
      event.stopPropagation();
    }

    // Check if claim has a valid partner with ID
    if (!claim?.Partner?.ID) {
      this.parent.openSnackBar('No partner associated with this claim', 'OK', 'alert-info-snackbar');
      return;
    }

    try {
      // Encode the partner ID using IdObfuscator
      const encodedPartnerId = IdObfuscator.encode(claim.Partner.ID);

      // Build the public URL using current window origin
      const baseUrl = window.location.origin;
      const publicUrl = `${baseUrl}/external/claim?pid=${encodedPartnerId}`;

      // Copy to clipboard
      navigator.clipboard.writeText(publicUrl).then(() => {
        this.parent.openSnackBar('Public URL copied to clipboard', 'OK', 'light-info-snackbar');
      }).catch((err) => {
        console.error('Failed to copy URL to clipboard:', err);
        // Fallback: show URL in snackbar
        this.parent.openSnackBar('Failed to copy URL', 'OK', 'alert-info-snackbar');
      });
    } catch (error) {
      console.error('Error generating public URL:', error);
      this.parent.openSnackBar('Failed to generate public URL', 'OK', 'alert-info-snackbar');
    }
  }

  /**
   * Generate public URL for viewing a specific existing claim
   * URL format: /external/claim?pid={encodedPartnerId}&id={encodedClaimId}
   */
  generatePublicUrlForClaim(claim: any, event?: Event): void {
    // Stop event propagation to prevent opening claim details
    if (event) {
      event.stopPropagation();
    }

    // Check if claim has both a valid partner with ID and a claim ID
    if (!claim?.Partner?.ID || !claim?.ID) {
      this.parent.openSnackBar('Invalid claim data', 'OK', 'alert-info-snackbar');
      return;
    }

    try {
      // Encode both partner ID and claim ID
      const encodedPartnerId = IdObfuscator.encode(claim.Partner.ID);
      const encodedClaimId = IdObfuscator.encode(claim.ID);

      // Build the public URL with both parameters
      const baseUrl = window.location.origin;
      const publicUrl = `${baseUrl}/external/claim?pid=${encodedPartnerId}&id=${encodedClaimId}`;

      // Copy to clipboard
      navigator.clipboard.writeText(publicUrl).then(() => {
        this.parent.openSnackBar('Public URL copied to clipboard', 'OK', 'light-info-snackbar');
      }).catch((err) => {
        console.error('Failed to copy URL to clipboard:', err);
        this.parent.openSnackBar('Failed to copy URL', 'OK', 'alert-info-snackbar');
      });
    } catch (error) {
      console.error('Error generating public URL for claim:', error);
      this.parent.openSnackBar('Failed to generate public URL', 'OK', 'alert-info-snackbar');
    }
  }
}
